import { createSlice } from "@reduxjs/toolkit";
import {
  AdminData,
  NewUserViewingPageUpdate,
  PageViewer,
} from "../types/admin";

const initialState = {
  viewersByPage: {},
  logs: [],
  logDateRange: { start: 0, end: Infinity },
  connectedServices: [],
  wsConnected: false,
  serverTime: null,
} as AdminData;

function mergeUserViewingPageUpdate(
  state: AdminData,
  userViewingPageUpdate: NewUserViewingPageUpdate,
) {
  const { page, user, role, connectionId, status } = userViewingPageUpdate;
  if (status) {
    if (!state.viewersByPage[page]) {
      state.viewersByPage[page] = [];
    }
    if (
      !state.viewersByPage[page].some((v) => v.connectionId === connectionId)
    ) {
      state.viewersByPage[page].push({ user, role, connectionId });
    }
  } else {
    if (state.viewersByPage[page]) {
      const index = state.viewersByPage[page].findIndex(
        (v) => v.connectionId === connectionId,
      );
      if (index !== -1) {
        state.viewersByPage[page].splice(index, 1);
      }
    }
    if (state.viewersByPage[page] && state.viewersByPage[page].length === 0) {
      delete state.viewersByPage[page];
    }
  }
}

// When sending a query to fetch the users viewing pages, a subscription notifying a new viewer may arrive before the query response,
// yet the user wasn't in the server when the query was processed by the backend, causing the viewer to be missing from the query response and not appear in the list.
// After a query response we can merge in any subscription responses received whilst the query is in-flight to mitigate this
function mergeUsersViewingPages(
  state: AdminData,
  usersViewingPages: { page: string; viewers: PageViewer[] }[],
) {
  for (const pageData of usersViewingPages) {
    if (!state.viewersByPage[pageData.page]) {
      state.viewersByPage[pageData.page] = pageData.viewers;
    } else {
      for (const viewer of pageData.viewers) {
        if (
          !state.viewersByPage[pageData.page].some(
            (v) => v.connectionId === viewer.connectionId,
          )
        ) {
          state.viewersByPage[pageData.page].push(viewer);
        }
      }
    }
  }
}

export const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setWsConnectedState: (state, action) => {
      state.wsConnected = action.payload;
    },
    resetUsersViewingPages: (state) => {
      state.viewersByPage = {};
    },
    setUsersViewingPages: (state, action) => {
      mergeUsersViewingPages(state, action.payload);
    },
    setUserViewingPageUpdate: (state, action) => {
      mergeUserViewingPageUpdate(state, action.payload);
    },
    setLogs: (state, action) => {
      state.logs = action.payload;
    },
    addLog: (state, action) => {
      if (
        action.payload.date >= state.logDateRange.start &&
        action.payload.date <= state.logDateRange.end
      ) {
        state.logs.push(action.payload);
      }
    },
    setLogDateRange: (state, action) => {
      state.logDateRange = action.payload;
    },
    setConnectedServices: (state, action) => {
      state.connectedServices = action.payload;
    },
    setConnectedServiceUpdate: (state, action) => {
      const { name, connections, deployments } = action.payload;
      const index = state.connectedServices.findIndex(
        (service) => service.name === name,
      );
      if (index !== -1) {
        state.connectedServices[index].connections = connections;
        state.connectedServices[index].deployments = deployments;
      } else {
        state.connectedServices.push({
          name,
          connections: connections,
          deployments: deployments,
        });
      }
    },
    setServerTime: (state, action) => {
      state.serverTime = action.payload;
    },
  },
});

export const {
  setWsConnectedState,
  resetUsersViewingPages,
  setUsersViewingPages,
  setUserViewingPageUpdate,
  setLogs,
  addLog,
  setLogDateRange,
  setConnectedServices,
  setConnectedServiceUpdate,
  setServerTime,
} = adminSlice.actions;
export default adminSlice.reducer;
